import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, Info, FileSearch, Eye, Shield, ScanFace } from 'lucide-react';
import { useVerificationConfig, useUpdateVerificationConfig } from '../../hooks/useSettings';
import { usePricing, formatPricePerUnit } from '../../hooks/usePricing';

const DEFAULTS = {
  ocr_tier: 'basic',
  liveness_tier: 'basic',
  liveness_mode: 'active',
  face_matching_tier: 'basic',
  fraud_tier: 'basic',
};

// Static tier structure - prices are filled in dynamically from the API
const BASE_TIER_OPTIONS = {
  ocr: [
    {
      value: 'basic',
      featureId: 'id_verification',
      label: 'Basic',
      accuracy: '~85%',
      description: 'Fast text extraction for common document formats',
      unit: 'doc',
    },
    {
      value: 'premium',
      featureId: 'document_ocr_premium',
      label: 'Premium',
      accuracy: '~98%',
      description: 'High-accuracy extraction with handwriting support',
      unit: 'doc',
    },
  ],
  liveness: [
    {
      value: 'basic',
      featureId: 'passive_liveness',
      label: 'Basic',
      accuracy: '~92%',
      description: 'Detects photos and basic spoofing attempts',
      unit: 'check',
    },
    {
      value: 'premium',
      featureId: 'active_liveness',
      label: 'Premium',
      accuracy: '~99%',
      description: 'Defeats masks, deepfakes, and advanced spoofs',
      unit: 'check',
    },
  ],
  faceMatching: [
    {
      value: 'basic',
      featureId: 'face_matching_free',
      label: 'Basic',
      accuracy: '~95%',
      description: 'Open source face matching using DeepFace',
      unit: 'match',
    },
    {
      value: 'premium',
      featureId: 'face_matching',
      label: 'Premium',
      accuracy: '~99%',
      description: 'AWS Rekognition for highest accuracy matching',
      unit: 'match',
    },
  ],
};

const LIVENESS_MODES = [
  { value: 'active', label: 'Active (face challenges)', description: 'User turns head left/right and blinks' },
  { value: 'passive', label: 'Passive (anti-spoof)', description: 'Analyzes video frames for spoofing' },
  { value: 'both', label: 'Both', description: 'Run both active and passive checks' },
];

export default function VerificationSettings() {
  const { data: config, isLoading } = useVerificationConfig();
  const { data: pricing, isLoading: pricingLoading } = usePricing();
  const updateConfig = useUpdateVerificationConfig();

  const [formData, setFormData] = useState(DEFAULTS);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Build tier options with dynamic prices from the API
  const TIER_OPTIONS = useMemo(() => {
    const features = pricing?.features || [];
    const getFeature = (id) => features.find((f) => f.id === id);

    return {
      ocr: BASE_TIER_OPTIONS.ocr.map((tier) => ({
        ...tier,
        price: formatPricePerUnit(getFeature(tier.featureId), tier.unit),
      })),
      liveness: BASE_TIER_OPTIONS.liveness.map((tier) => ({
        ...tier,
        price: formatPricePerUnit(getFeature(tier.featureId), tier.unit),
      })),
      faceMatching: BASE_TIER_OPTIONS.faceMatching.map((tier) => ({
        ...tier,
        price: formatPricePerUnit(getFeature(tier.featureId), tier.unit),
      })),
    };
  }, [pricing]);

  useEffect(() => {
    if (config) {
      setFormData({
        ocr_tier: config.ocr_tier || DEFAULTS.ocr_tier,
        liveness_tier: config.liveness_tier || DEFAULTS.liveness_tier,
        liveness_mode: config.liveness_mode || DEFAULTS.liveness_mode,
        face_matching_tier: config.face_matching_tier || DEFAULTS.face_matching_tier,
        fraud_tier: config.fraud_tier || DEFAULTS.fraud_tier,
      });
    }
  }, [config]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaveError(null);
    try {
      await updateConfig.mutateAsync(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      setSaveError(
        typeof error === 'string' ? error : error?.message || 'Failed to save verification settings'
      );
    }
  };

  if (isLoading || pricingLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div className="verification-settings" data-testid="verification-settings">
      <style>{`
        .verification-settings {
          max-width: 700px;
        }

        .verification-section {
          margin-bottom: 28px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border-subtle, rgba(255,255,255,0.06));
        }

        .verification-section:last-of-type {
          border-bottom: none;
        }

        .verification-section-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 4px;
        }

        .verification-section-icon {
          color: var(--accent-primary);
        }

        .verification-section-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
        }

        .verification-section-desc {
          font-size: 13px;
          color: var(--text-secondary, #8b919e);
          margin: 0 0 16px;
        }

        .verification-tier-options {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .verification-tier-option {
          display: flex;
          flex-direction: column;
          padding: 16px;
          background: var(--bg-surface, rgba(255,255,255,0.03));
          border: 2px solid var(--border-subtle, rgba(255,255,255,0.06));
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .verification-tier-option:hover {
          border-color: var(--border-default, rgba(255,255,255,0.12));
        }

        .verification-tier-option.selected {
          border-color: var(--accent-primary);
          background: var(--accent-glow-subtle);
        }

        .verification-tier-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .verification-tier-name {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
        }

        .verification-tier-price {
          font-size: 12px;
          font-weight: 600;
          padding: 3px 8px;
          border-radius: 4px;
          background: var(--bg-elevated, rgba(255,255,255,0.08));
          color: var(--text-secondary, #8b919e);
        }

        .verification-tier-option.selected .verification-tier-price {
          background: var(--accent-primary);
          color: white;
        }

        .verification-tier-accuracy {
          font-size: 11px;
          font-weight: 600;
          color: var(--accent-primary);
          margin-bottom: 4px;
        }

        .verification-tier-desc {
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
          line-height: 1.4;
        }

        .verification-tier-radio {
          display: none;
        }

        .verification-mode-select {
          width: 100%;
          background: var(--bg-surface, rgba(255,255,255,0.03));
          border: 1px solid var(--border-subtle, rgba(255,255,255,0.1));
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 13px;
          color: var(--text-primary, #f0f2f5);
          cursor: pointer;
          margin-top: 12px;
        }

        .verification-mode-select option {
          background: var(--bg-primary, #1a1b23);
          color: var(--text-primary, #f0f2f5);
        }

        .verification-mode-hint {
          font-size: 11px;
          color: var(--text-tertiary, #5a5f6b);
          margin-top: 6px;
        }

        .verification-info-box {
          display: flex;
          gap: 10px;
          padding: 12px 14px;
          background: var(--accent-glow-subtle);
          border: 1px solid var(--accent-glow);
          border-radius: 8px;
          margin-bottom: 24px;
        }

        .verification-info-box p {
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
          margin: 0;
          line-height: 1.5;
        }

        .verification-disabled-note {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--text-muted, #666);
          margin-top: 12px;
          padding: 10px 12px;
          background: var(--bg-surface, rgba(255,255,255,0.02));
          border-radius: 6px;
        }

        .verification-save-bar {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid var(--border-subtle, rgba(255,255,255,0.06));
        }

        .verification-save-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: var(--accent-primary);
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: opacity 0.15s ease;
        }

        .verification-save-btn:hover {
          opacity: 0.9;
        }

        .verification-save-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .verification-save-success {
          font-size: 13px;
          color: var(--status-approved, #34d399);
        }

        .verification-save-error {
          font-size: 13px;
          color: var(--status-rejected, #f87171);
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="verification-info-box">
        <Info size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0, marginTop: 1 }} />
        <p>
          Configure which verification services to use. Basic options are free but may have lower accuracy.
          These are tenant-wide defaults. You can override per-workflow in the Workflow Builder.
        </p>
      </div>

      {/* OCR Section */}
      <div className="verification-section">
        <div className="verification-section-header">
          <FileSearch size={18} className="verification-section-icon" />
          <h3 className="verification-section-title">Document OCR</h3>
        </div>
        <p className="verification-section-desc">Extract text from ID documents and passports</p>
        <div className="verification-tier-options">
          {TIER_OPTIONS.ocr.map((option) => (
            <label
              key={option.value}
              className={`verification-tier-option ${formData.ocr_tier === option.value ? 'selected' : ''}`}
              data-testid={`ocr-tier-${option.value}`}
            >
              <input
                type="radio"
                name="ocr_tier"
                value={option.value}
                checked={formData.ocr_tier === option.value}
                onChange={() => handleChange('ocr_tier', option.value)}
                className="verification-tier-radio"
              />
              <div className="verification-tier-header">
                <span className="verification-tier-name">{option.label}</span>
                <span className="verification-tier-price">{option.price}</span>
              </div>
              <div className="verification-tier-accuracy">{option.accuracy} accuracy</div>
              <span className="verification-tier-desc">{option.description}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Liveness Section */}
      <div className="verification-section">
        <div className="verification-section-header">
          <Eye size={18} className="verification-section-icon" />
          <h3 className="verification-section-title">Liveness Detection</h3>
        </div>
        <p className="verification-section-desc">Verify the user is a real person, not a photo or video</p>
        <div className="verification-tier-options">
          {TIER_OPTIONS.liveness.map((option) => (
            <label
              key={option.value}
              className={`verification-tier-option ${formData.liveness_tier === option.value ? 'selected' : ''}`}
              data-testid={`liveness-tier-${option.value}`}
            >
              <input
                type="radio"
                name="liveness_tier"
                value={option.value}
                checked={formData.liveness_tier === option.value}
                onChange={() => handleChange('liveness_tier', option.value)}
                className="verification-tier-radio"
              />
              <div className="verification-tier-header">
                <span className="verification-tier-name">{option.label}</span>
                <span className="verification-tier-price">{option.price}</span>
              </div>
              <div className="verification-tier-accuracy">{option.accuracy} accuracy</div>
              <span className="verification-tier-desc">{option.description}</span>
            </label>
          ))}
        </div>

        {/* Liveness Mode (only for basic tier) */}
        {formData.liveness_tier === 'basic' && (
          <>
            <select
              className="verification-mode-select"
              value={formData.liveness_mode}
              onChange={(e) => handleChange('liveness_mode', e.target.value)}
              data-testid="liveness-mode-select"
            >
              {LIVENESS_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
            <div className="verification-mode-hint">
              {LIVENESS_MODES.find((m) => m.value === formData.liveness_mode)?.description}
            </div>
          </>
        )}
      </div>

      {/* Face Matching Section */}
      <div className="verification-section">
        <div className="verification-section-header">
          <ScanFace size={18} className="verification-section-icon" />
          <h3 className="verification-section-title">Face Matching</h3>
        </div>
        <p className="verification-section-desc">Compare selfie to ID photo to verify identity</p>
        <div className="verification-tier-options">
          {TIER_OPTIONS.faceMatching.map((option) => (
            <label
              key={option.value}
              className={`verification-tier-option ${formData.face_matching_tier === option.value ? 'selected' : ''}`}
              data-testid={`face-matching-tier-${option.value}`}
            >
              <input
                type="radio"
                name="face_matching_tier"
                value={option.value}
                checked={formData.face_matching_tier === option.value}
                onChange={() => handleChange('face_matching_tier', option.value)}
                className="verification-tier-radio"
              />
              <div className="verification-tier-header">
                <span className="verification-tier-name">{option.label}</span>
                <span className="verification-tier-price">{option.price}</span>
              </div>
              <div className="verification-tier-accuracy">{option.accuracy} accuracy</div>
              <span className="verification-tier-desc">{option.description}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Fraud Detection Section */}
      <div className="verification-section">
        <div className="verification-section-header">
          <Shield size={18} className="verification-section-icon" />
          <h3 className="verification-section-title">Fraud Detection</h3>
        </div>
        <p className="verification-section-desc">Detect manipulated or synthetic documents</p>
        <div className="verification-tier-options" style={{ gridTemplateColumns: '1fr' }}>
          <div className="verification-tier-option selected" style={{ cursor: 'default' }}>
            <div className="verification-tier-header">
              <span className="verification-tier-name">Basic</span>
              <span className="verification-tier-price">Free</span>
            </div>
            <span className="verification-tier-desc">
              ELA (Error Level Analysis) + Synthetic/Deepfake detection
            </span>
          </div>
        </div>
        <div className="verification-disabled-note">
          <Info size={14} />
          Premium fraud detection coming soon
        </div>
      </div>

      {/* Save Bar */}
      <div className="verification-save-bar">
        <button
          className="verification-save-btn"
          onClick={handleSave}
          disabled={updateConfig.isPending}
          data-testid="save-verification-btn"
        >
          {updateConfig.isPending && (
            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
          )}
          {updateConfig.isPending ? 'Saving...' : 'Save Configuration'}
        </button>
        {saveSuccess && <span className="verification-save-success">Settings saved</span>}
        {saveError && <span className="verification-save-error">{saveError}</span>}
      </div>
    </div>
  );
}
